<script lang="ts" setup>
import { computed } from 'vue';

/**
 * ideally this would be in types.ts alongside ButtonSize etc
 * but currently Vue3 can't use imported types in defineProps
 * see  https://github.com/vuejs/rfcs/blob/master/active-rfcs/0040-script-setup.md#type-only-propsemit-declarations
 */

// TODO: Rewrite style props as a singular theme/action prop

export type ButtonProps = {
  label?: string;
  reversed?: boolean;
  translucent?: boolean;
  transparent?: boolean;
  disabled?: boolean;
  shape?: 'pill' | 'circle';
  size?: 'small' | 'medium' | 'large';
  iconPosition?: 'right' | 'left';
  link?: string;
  linkTarget?: string;
  linkRel?: string;
  ariaLabel?: string;
};

const props = withDefaults(defineProps<ButtonProps>(), {
  shape: 'pill',
  linkTarget: '_self',
  ariaLabel: undefined,
});

const isExternalLink = computed(() => {
  return props.link && props.link.startsWith('http');
});

const CSSClasses = computed(() => [
  props.reversed && `button--reversed`,
  props.translucent && 'button--translucent',
  props.disabled && 'button--disabled',
  `button--${props.shape}`,
  props.iconPosition && `button--icon-${props.iconPosition}`,
  props.size && `button--${props.size}`,
]);
</script>

<template>
  <a
    v-if="isExternalLink"
    :class="CSSClasses"
    :href="link"
    :target="linkTarget"
    :rel="linkRel"
    class="button"
  >
    <span class="button__label">{{ label }}</span>
    <slot />
  </a>
  <router-link
    v-else-if="!!link && !isExternalLink"
    :class="CSSClasses"
    :to="link"
    :target="linkTarget"
    class="button"
  >
    <span class="button__label">{{ label }}</span>
    <slot />
  </router-link>

  <button
    v-else
    :class="CSSClasses"
    class="button"
    type="button"
    :disabled="disabled"
    :aria-label="ariaLabel"
  >
    <span v-if="label" class="button__label">{{ label }}</span>
    <slot />
  </button>
</template>

<style lang="scss">
@use 'sass:color';
@use 'core/colors';
@use 'core/layout/grid-vars';
@use 'core/responsive';
@use 'core/typography/fonts';
@use 'core/typography/type';
@use 'core/util';
@use 'vendor/bootstrap/scss/mixins/breakpoints';

:root {
  --button-pad-top: #{grid-vars.space(0.8)};
  --button-pad-bottom: #{grid-vars.space(1)};
  --button-pad-horiz: #{grid-vars.space(1.9)};
  --button-pad-top-lg: #{grid-vars.space(2.5)};
  --button-pad-bottom-lg: #{grid-vars.space(2.6)};
  --button-pad-horiz-lg: #{grid-vars.space(4)};
  --button-icon-size: #{util.rem(20)};
}

@mixin button-colors-reversed($translucent) {
  background-color: var(--collection-text-secondary);

  @if $translucent {
    color: color.adjust(colors.color(action), $alpha: -0.1);
    stroke: color.adjust(colors.color(action), $alpha: -0.1);
  } @else {
    color: colors.color(negative);
    fill: transparent;
    stroke: colors.color(negative);
  }
}

@mixin button-colors($translucent) {
  @if $translucent {
    background-color: var(--collection-text-translucent);
  } @else {
    background-color: colors.color(negative);
  }

  color: var(--collection-text-primary);
  fill: transparent;
  stroke: var(--collection-text-primary);
}

@mixin button-color-styles($reversed: false, $translucent: false) {
  @if $reversed {
    @include button-colors-reversed($translucent);

    &:hover {
      background-color: var(--collection-text-primary);
    }
  } @else {
    @include button-colors($translucent);

    &:hover {
      @include button-colors-reversed($translucent);

      background-color: var(--collection-text-primary);
    }
  }
}

.button {
  $self: &;

  --color-text: colors.color(negative);

  @include type.text--mono;

  @include button-color-styles;

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: grid-vars.space(1.1) var(--button-pad-horiz) grid-vars.space(1.4);

  @include breakpoints.media-breakpoint-up(md) {
    min-height: grid-vars.space(4.5);
    padding: var(--button-pad-top) var(--button-pad-horiz) var(--button-pad-bottom);
  }

  &:not(:last-child) {
    margin-right: grid-vars.space(1);
  }

  img,
  svg {
    @include util.size(var(--button-icon-size), var(--button-icon-size));
  }

  br {
    @include responsive.show-on(phone);
  }

  &#{$self}--link {
    @include fonts.font-size(16);

    display: inline-flex;
  }

  &#{$self}--reversed {
    @include button-color-styles(true);
  }

  &#{$self}--circle {
    @include util.borders-sphere;

    width: grid-vars.space(4.5);
    height: grid-vars.space(4.5);

    svg,
    .image-media {
      @include util.position-absolute-center;

      width: 50%;
    }
  }

  &#{$self}--translucent {
    @include button-color-styles(false, true);

    &:hover {
      color: colors.color(background);

      svg {
        path {
          stroke: colors.color(background);
        }
      }
    }
  }

  &#{$self}--transparent {
    &.button {
      background-color: transparent;

      &:hover {
        background-color: transparent;
      }
    }
  }

  &#{$self}--disabled,
  &:disabled {
    pointer-events: none;
    cursor: default;
    opacity: 0.6;
    stroke: currentcolor;
  }

  &.button--danger {
    color: colors.color(bad, secondary);
    background-color: color.adjust(colors.color(bad), $alpha: -0.9);

    &:hover {
      color: colors.color(negative);
      background-color: colors.color(bad);
    }

    &.button--reversed {
      color: colors.color(negative);
      background-color: colors.color(bad);

      &:hover {
        color: colors.color(bad, secondary);
        background-color: color.adjust(colors.color(bad), $alpha: -0.9);
      }
    }
  }

  &#{$self}--pill {
    @include util.borders-convex;
  }

  &#{$self}--small {
    height: calc(var(--space-relative) * 3);
    min-height: 0;
    @include fonts.font-size(12);

    svg {
      width: calc(var(--space-relative) * 1.5);
      height: calc(var(--space-relative) * 1.5);
    }
  }

  &#{$self}--large {
    padding: var(--button-pad-top-lg) var(--button-pad-horiz-lg) var(--button-pad-bottom-lg);

    @include responsive.media-query(phone) {
      padding: grid-vars.space(2) var(--button-pad-horiz-lg) grid-vars.space(2.2);
    }
  }

  &#{$self}--icon-left {
    @at-root .button--icon-left .button__label {
      order: 2;
      margin-left: grid-vars.space(0.6);
    }
  }

  &#{$self}--icon-right {
    @at-root .button--icon-right .button__label {
      margin-right: grid-vars.space(0.6);
    }
  }

  &#{$self}__label {
    display: flex;
    align-items: center;
    padding-top: 1px;
    text-align: center;
  }
}

.button-group {
  display: flex;
  align-items: flex-start;

  &.button-group-row {
    flex-direction: column;

    .button {
      &:not(:first-child) {
        margin-top: 0.75rem;
      }
    }
  }

  &.button-group-separator {
    padding-top: 1rem;
    border-top: 1px solid colors.color(background);
  }
}

.button-inline {
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;

  &.button-primary {
    color: colors.color(good, primary);
  }
  &.button-secondary {
    color: colors.color(active, primary);
  }
}
</style>
